import React, { useState, useRef } from 'react';
import { shareReportAsPdf, downloadReportAsPdf } from '../../../utils/reportPdfShare';
import { CheckCircle2, ShieldCheck, Copy, Check, Share2, Download } from 'lucide-react';

// ── SugarHathi brand palette, matching do_finance_new.jsx (GRAD).
const ACCENT = '#f97316';
const ACCENT_DARK = '#ea580c';
const BG = '#fffbf5';
const INK = '#1f2937';
const SLATE = '#6b7280';
const LINE = '#f0e6d8';
const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const SAMPLE = {
  status: 'SUCCESS',
  message: 'KYC Details for PAN retrieved successfully',
  kycResult: {
    email: 'kr************na@gmail.com',
    gender: 'M',
    mobile: '95XXXXXX24',
    address: {
      city: 'Rajendranagar',
      full: 'Flat No 104, B Block, Vaishnavi Oasis Apartments, Bandlaguda Jagir, Kismatpur Road, Don Bosco Nagar S.O, Rajendranagar, K.V.RANGAREDDY, Rajendranagar, 500086, Telangana, India',
      state: 'Telangana',
      country: 'India',
      pincode: '500086',
    },
    panType: 'Person',
    category: 'INDIVIDUAL OR PERSON',
    fullName: 'KRISHNA KUMAR MENA',
    idNumber: 'APPPM4486G',
    idStatus: 'VALID',
    lastName: 'MENA',
    firstName: 'KRISHNA KUMAR',
    panStatus: 'VALID',
    dateOfBirth: '05-05-1982',
    maskedAadhaar: '93********85',
    aadhaarSeedingStatus: 'Successful',
  },
  decentroTxnId: '47BE549A86994EA7AC92E81A3FB9B641',
  responseCode: 'S00000',
  requestTimestamp: '2026-08-07 16:13:00.719807 IST (GMT +0530)',
  responseTimestamp: '2026-08-07 16:13:04.430877 IST (GMT +0530)',
};

function StatusPill({ label }) {
  const ok = /VALID|SUCCESS|Successful/i.test(label);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 999,
      fontSize: 11.5, fontWeight: 700, background: ok ? '#f0fdf4' : '#fef2f2',
      color: ok ? '#15803d' : '#b91c1c', border: `1px solid ${ok ? '#bbf7d0' : '#fecaca'}`,
    }}>
      {ok && <CheckCircle2 size={12} strokeWidth={2.75} />}
      {label}
    </span>
  );
}

function Row({ label, value, mono, action }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16,
      padding: '6px 0', borderBottom: `1px solid ${LINE}`,
    }}>
      <span style={{ fontSize: 12, color: SLATE, fontWeight: 500, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: 12.5, color: INK, fontWeight: 600, textAlign: 'right',
        fontFamily: mono ? MONO : 'inherit', wordBreak: 'break-word',
        display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end',
      }}>
        {value ?? '—'}
        {action}
      </span>
    </div>
  );
}

function SectionCard({ title, children, footer }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${LINE}`, borderRadius: 14, padding: '14px 16px', marginBottom: 8 }}>
      <h3 style={{ fontSize: 12.5, fontWeight: 800, color: ACCENT_DARK, margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </h3>
      <div>{children}</div>
      {footer && <div style={{ paddingTop: 12, marginTop: 4 }}>{footer}</div>}
    </div>
  );
}

export default function PanAdminFinal({ data = SAMPLE }) {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const k = data.kycResult;
  const a = k.address;

  const copyTxnId = () => {
    navigator.clipboard?.writeText(data.decentroTxnId).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // "Download" triggers the browser's print-to-PDF dialog. "Share" uses the
  // native Web Share API (works on mobile browsers); falls back to print if
  // the browser doesn't support navigator.share.
  const [pdfBusy, setPdfBusy] = useState(false);
  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try { await downloadReportAsPdf(cardRef, 'pan-plus-admin-report.pdf', BG); } finally { setPdfBusy(false); }
  };
  const handleShare = async () => {
    const shareText = `KYC Verification — ${k.fullName}\nPAN: ${k.idNumber}\nStatus: ${data.status}`;
    setPdfBusy(true);
    try { await shareReportAsPdf(cardRef, 'pan-plus-admin-report.pdf', 'PAN Plus — Admin Report', shareText, BG); } finally { setPdfBusy(false); }
  };

  return (
    <div style={{ minHeight: '100%', background: BG, padding: '24px 14px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <style>{`
        @media print { .no-print { display: none !important; } body { background: #fff !important; } }
      `}</style>
      <div ref={cardRef} style={{ maxWidth: 560, width: '100%', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button
            onClick={handleShare} disabled={pdfBusy}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px',
              color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Share2 size={13} />
            Share
          </button>
          <button
            onClick={handleDownloadPdf} disabled={pdfBusy}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              background: '#fff', border: `1px solid ${LINE}`, borderRadius: 10, padding: '9px',
              color: ACCENT_DARK, fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
            }}
          >
            <Download size={13} />
            Download as PDF
          </button>
        </div>

        {/* Header */}
        <div style={{
          background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_DARK} 100%)`,
          borderRadius: 20, padding: '20px 22px', position: 'relative', overflow: 'hidden', marginBottom: 14,
        }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                <ShieldCheck size={16} color="#fff" strokeWidth={2.5} style={{ flexShrink: 0 }} />
                <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                  KYC Verification Report
                </span>
              </div>
              <h1 style={{ color: '#fff', fontSize: 19, fontWeight: 800, margin: 0, wordBreak: 'break-word' }}>{k.fullName}</h1>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12.5, margin: '3px 0 0', fontFamily: MONO }}>
                {k.idNumber}
              </p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10, padding: '5px 11px', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <CheckCircle2 size={12} color="#fff" strokeWidth={3} />
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 800 }}>{data.status}</span>
            </div>
          </div>
        </div>

        {/* Identity — row-wise */}
        <SectionCard
          title="Identity"
          footer={
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill label={`PAN: ${k.panStatus}`} />
              <StatusPill label={`ID: ${k.idStatus}`} />
              <StatusPill label={`Aadhaar: ${k.aadhaarSeedingStatus}`} />
            </div>
          }
        >
          <Row label="First Name" value={k.firstName} />
          <Row label="Last Name" value={k.lastName} />
          <Row label="Date of Birth" value={k.dateOfBirth} mono />
          <Row label="Gender" value={k.gender === 'M' ? 'Male' : k.gender === 'F' ? 'Female' : k.gender} />
          <Row label="PAN Type" value={k.panType} />
          <Row label="Category" value={k.category} />
        </SectionCard>

        {/* Contact & Aadhaar — row-wise */}
        <SectionCard title="Contact & Aadhaar">
          <Row label="Email" value={k.email} mono />
          <Row label="Mobile" value={k.mobile} mono />
          <Row label="Masked Aadhaar" value={k.maskedAadhaar} mono />
        </SectionCard>

        {/* Address — row-wise */}
        <SectionCard title="Registered Address">
          <Row label="Full Address" value={a.full} />
          <Row label="City" value={a.city} />
          <Row label="State" value={a.state} />
          <Row label="Pincode" value={a.pincode} mono />
          <Row label="Country" value={a.country} />
        </SectionCard>

        {/* Transaction metadata — row-wise (admin only, kept for audit trail) */}
        <SectionCard title="Transaction Metadata">
          <Row
            label="Vendor Txn ID"
            value={data.decentroTxnId}
            mono
            action={
              <button onClick={copyTxnId} style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#15803d' : ACCENT_DARK, display: 'inline-flex' }}>
                {copied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            }
          />
          <Row label="Response Code" value={data.responseCode} mono />
          <Row label="Requested" value={data.requestTimestamp} mono />
          <Row label="Responded" value={data.responseTimestamp} mono />
        </SectionCard>
      </div>
    </div>
  );
}
